/**
 * Little Diary — Checkout shipping prefill (recipient contact test v2)
 *
 * On normal cart checkout click, reads the current cart (/cart.js), finds the
 * main hamper line item carrying "Recipient Name" / "Recipient WhatsApp No."
 * line-item properties, and uses those values ONLY to prefill the Shopify
 * checkout SHIPPING (delivery) address fields (first name, last name, phone).
 *
 * The phone value is normalized ONLY for the checkout-prefill query param.
 * The original "Recipient WhatsApp No." line-item property is never
 * modified — this script only reads it.
 *
 * Does not touch billing, purchaser data, payment logic, or line items.
 * Customer can still edit every prefilled field normally in checkout.
 *
 * HARDENED (resilience audit fix, CRITICAL #2): this is the actual code
 * path a real customer's Checkout click runs through. This script
 * intercepts #checkout's click in the CAPTURE phase and calls
 * preventDefault() unconditionally — which cancels the button's implicit
 * form submission, meaning the separate orphan-sweep guard in
 * sections/main-cart-items.liquid (which listens for the form's 'submit'
 * event) never actually fires for a real click, since that event is never
 * dispatched. The orphan check now runs HERE instead — using the exact
 * same _Role/_Set classification rules main-cart-items.liquid uses,
 * reusing the cart read this script already performs — and completes
 * BEFORE the checkout URL is built or the redirect happens.
 */
(function () {
  if (window.__ldCheckoutPrefillBound) return;
  window.__ldCheckoutPrefillBound = true;

  // UPDATED: no longer guesses which part of a name is the first name vs
  // surname — Malaysian, Chinese, Indian, Malay and international naming
  // formats don't reliably split on "last word = surname". The entire
  // Recipient Name is used as the checkout last name only; first name is
  // always left blank for the customer to fill in if they want to.
  function recipientNameForCheckout(fullName) {
    var trimmed = (fullName || '').toString().trim().replace(/\s+/g, ' ');
    return { firstName: '', lastName: trimmed };
  }

  // Normalizes a recipient WhatsApp number into an E.164-ish value for the
  // checkout phone prefill ONLY. Never mutates the original property value.
  // Returns null when the country can't be safely identified — in that case
  // the checkout phone field is simply left unprefilled.
  function normalizePhoneForCheckout(rawPhone) {
    if (!rawPhone) return null;
    var cleaned = rawPhone.toString().replace(/[\s\-()]/g, '');
    if (!cleaned) return null;

    // Already international — preserve whatever country code it has.
    if (cleaned.charAt(0) === '+') {
      return cleaned;
    }

    // Malaysian local number, e.g. 0123456789 -> +60123456789
    if (cleaned.charAt(0) === '0') {
      return '+60' + cleaned.slice(1);
    }

    // Malaysian number without a plus, e.g. 60123456789 -> +60123456789
    if (cleaned.indexOf('60') === 0) {
      return '+' + cleaned;
    }

    // Singapore number without a plus, e.g. 6591234567 -> +6591234567
    if (cleaned.indexOf('65') === 0) {
      return '+' + cleaned;
    }

    // Anything else: don't guess a country code.
    return null;
  }

  function findRecipientProperties(cart) {
    if (!cart || !Array.isArray(cart.items)) return null;
    for (var i = 0; i < cart.items.length; i++) {
      var properties = cart.items[i].properties;
      if (
        properties &&
        properties['Recipient Name'] &&
        properties['Recipient WhatsApp No.']
      ) {
        return properties;
      }
    }
    return null;
  }

  function buildCheckoutUrl(properties) {
    var name = recipientNameForCheckout(properties['Recipient Name']);
    var phone = normalizePhoneForCheckout(properties['Recipient WhatsApp No.']);
    var params = new URLSearchParams();

    if (name.firstName) {
      params.set('checkout[shipping_address][first_name]', name.firstName);
    }
    if (name.lastName) {
      params.set('checkout[shipping_address][last_name]', name.lastName);
    }
    if (phone) {
      params.set('checkout[shipping_address][phone]', phone);
    }

    var query = params.toString();
    return query ? '/cart/checkout?' + query : '/cart/checkout';
  }

  // ---- orphan-integrity check ----
  // Deliberately duplicated (under distinct ld*Prefill*/ldFind*/ldEnsure*
  // names, never the same identifiers) rather than shared with
  // sections/main-cart-items.liquid's ldItemSet/ldIsAddonItem/
  // ldSweepOrphanAddons/ldFetchJsonWithOneRetry — Shopify theme assets
  // have no module system, and this file loads as a separate <script src>
  // in the same global scope as that inline script, so reusing the exact
  // same names would silently let whichever script parses second
  // redefine the other's functions. Kept behaviourally identical to that
  // proven logic on purpose.

  function ldPrefillItemSet(item) {
    var props = item.properties || {};
    return props._Set || props.Set || props.Gift || '';
  }

  // PRIMARY signal: the explicit _Role property ("hamper"/"addon") written
  // by the product-page add-to-cart JS. FALLBACK: title-text, only for a
  // cart line from before _Role existed — same rule as main-cart-items.liquid.
  function ldPrefillIsAddonItem(item) {
    var props = item.properties || {};
    if (props._Role === 'addon') return true;
    if (props._Role === 'hamper') return false;
    var title = item.product_title || item.title || '';
    return (
      title.indexOf('Add-on:') !== -1 ||
      title.indexOf('Add-on Gift:') !== -1 ||
      title.indexOf('Driver Delivery Add-on:') !== -1
    );
  }

  // Transient: network-level failure (status 0), rate-limited (429), or a
  // Shopify-side error (5xx). A resolved 4xx is a genuine response and is
  // never retried.
  function ldPrefillIsTransientStatus(status) {
    return status === 0 || status === 429 || (status >= 500 && status <= 599);
  }

  function ldPrefillFetchJsonOnce(url, opts) {
    return fetch(url, opts)
      .then(function (response) {
        return response
          .json()
          .catch(function () { return null; })
          .then(function (data) {
            return { ok: response.ok, status: response.status, data: data };
          });
      })
      .catch(function () {
        return { ok: false, status: 0, data: null };
      });
  }

  // Retries EXACTLY once, only for a transient condition — safe because
  // the write this is used for (/cart/update.js, zeroing specific keys)
  // is idempotent by line key.
  function ldPrefillFetchJsonWithOneRetry(url, opts) {
    return ldPrefillFetchJsonOnce(url, opts).then(function (result) {
      if (result.ok || !ldPrefillIsTransientStatus(result.status)) return result;
      return ldPrefillFetchJsonOnce(url, opts);
    });
  }

  // Given a /cart.js snapshot, returns the keys of every add-on line whose
  // _Set has no surviving hamper line in the SAME snapshot (empty array if
  // the cart is already clean).
  function ldFindOrphanKeys(cart) {
    var hamperSets = {};
    cart.items.forEach(function (item) {
      if (ldPrefillIsAddonItem(item)) return;
      var set = ldPrefillItemSet(item);
      if (set) hamperSets[set] = true;
    });
    var orphanKeys = [];
    cart.items.forEach(function (item) {
      if (!ldPrefillIsAddonItem(item)) return;
      var set = ldPrefillItemSet(item);
      if (!set || !hamperSets[set]) orphanKeys.push(item.key);
    });
    return orphanKeys;
  }

  // Reads the cart, removes any orphan add-on (one retry on a transient
  // failure, then one read-only confirmation before giving up — the write
  // may have actually reached Shopify even if its response was lost),
  // and resolves with the FINAL, confirmed cart to build the checkout URL
  // from.
  //
  //  - Read fails entirely (cart state unknown): resolves `null` — the
  //    caller fails OPEN (proceeds to /cart/checkout unprefilled), same
  //    documented decision already made for the main-cart-items.liquid
  //    checkout guard — there is no evidence of an inconsistent cart in
  //    this case, only an unknown one.
  //  - No orphans, or orphans successfully cleaned: resolves the clean
  //    cart.
  //  - A definite orphan that could NOT be cleaned up even after retry +
  //    confirmation: resolves `{ blocked: true }` — the caller must NOT
  //    redirect to checkout with a known-inconsistent cart in this case.
  function ldEnsureCleanCartBeforeCheckout() {
    return ldPrefillFetchJsonWithOneRetry('/cart.js', { credentials: 'same-origin' }).then(function (readResult) {
      if (!readResult.ok) return null;

      var cart = readResult.data;
      var orphanKeys = ldFindOrphanKeys(cart);
      if (orphanKeys.length === 0) return cart;

      var updates = {};
      orphanKeys.forEach(function (key) { updates[key] = 0; });

      return ldPrefillFetchJsonWithOneRetry('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates }),
      }).then(function (writeResult) {
        if (writeResult.ok) return writeResult.data;

        return ldPrefillFetchJsonOnce('/cart.js', { credentials: 'same-origin' }).then(function (confirm) {
          if (!confirm.ok) return { blocked: true };
          var stillOrphaned = ldFindOrphanKeys(confirm.data);
          return stillOrphaned.length === 0 ? confirm.data : { blocked: true };
        });
      });
    });
  }

  // Synchronous busy-state guard, same pattern proven on the cart page and
  // the product page — a rapid second click on Checkout while the
  // orphan-check/redirect is already in flight is a pure no-op.
  var ldCheckoutBusy = false;

  function handleCheckoutClick(event) {
    var button = event.target.closest('#checkout, #CartDrawer-Checkout, #CartNotification-Checkout');
    if (!button || button.disabled) return;

    event.preventDefault();
    event.stopPropagation();

    if (ldCheckoutBusy) return;
    ldCheckoutBusy = true;

    ldEnsureCleanCartBeforeCheckout()
      .then(function (cart) {
        if (cart && cart.blocked) {
          // A known, confirmed orphan could not be cleaned up — never
          // proceed to checkout with a known-inconsistent cart. Sending
          // the customer back to /cart (rather than checkout) lets that
          // page's own independent page-load sweep make its own attempt.
          ldCheckoutBusy = false;
          window.location.href = '/cart';
          return;
        }
        var properties = cart ? findRecipientProperties(cart) : null;
        window.location.href = properties ? buildCheckoutUrl(properties) : '/cart/checkout';
      })
      .catch(function () {
        ldCheckoutBusy = false;
        window.location.href = '/cart/checkout';
      });
  }

  document.addEventListener('click', handleCheckoutClick, true);
})();
